
import Vue from "vue";
import { roofStructureTableColumns } from "./RoofStructureTable";
import { ITableWrapperColumns } from "@/types";
import NoResultsFound from "@/components/NotFound/NoResultsFound.vue";
import {
  roofStructureMapActions,
  roofStructureMapGetters
} from "@/store/modules/roofStructure";
import { cloneDeep } from "lodash";

interface IOptions {
  label: string;
  faIcon: string;
  class?: string;
  value: string;
}

interface IRoofStructureData {
  tableActions: any[];
  errorMessage: string;
  isLoading: boolean;
  isAdmin: boolean;
  loading: boolean;
  successMessage: string;
  roofStructureId: string;
  loadingText: string;
  selectedDocuments: any[];
  queryOverride: { deleted: boolean };
}

export default Vue.extend({
  name: "ListRoofStructures",
  components: {
    NoResultsFound
  },
  data(): IRoofStructureData {
    return {
      successMessage: "",
      errorMessage: "",
      isLoading: false,
      isAdmin: true,
      tableActions: [],
      loading: false,
      roofStructureId: "",
      loadingText: "",
      selectedDocuments: [],
      queryOverride: { deleted: false }
    };
  },
  methods: {
    ...roofStructureMapActions([
      "getRoofStructures",
      "deleteRoofStructure",
      "deleteBulkRoofStructures",
      "restoreBulkRoofStructures"
    ]),
    onToolbarItemClick(event: string) {
      switch (event) {
        case "createRoofStructure":
          this.$router.push("/admin/roofStructure/create");
          break;
        case "roofStructures-recycle-bin":
          this.queryOverride = { deleted: true };
          this.selectedDocuments = [];
          this.fetchPageData({});
          break;
        case "roofStructures":
          this.queryOverride = { deleted: false };
          this.selectedDocuments = [];
          this.fetchPageData({});
          break;
        case "roofStructures-activities":
          this.$router.push(`/admin/roofStructures/activities`).catch(() => {});
          break;
      }
    },
    async fetchPageData(queryObject: any): Promise<any> {
      try {
        this.loading = true;
        this.loadingText = "Loading roof structures. Please wait...";
        this.$emit("scrollTop");
        await this.getRoofStructures({
          query: { ...queryObject, ...this.queryOverride }
        });
      } catch (error) {
        this.errorMessage = error.message;
        this.$bugSnagClient.notify(error);
      } finally {
        this.loading = false;
        this.loadingText = "";
      }
    },
    async viewCallback(dataReceived: any): Promise<any> {
      if (this.viewingRecycleBin) return;
      const { _id } = dataReceived;
      this.$router.push(`/admin/${_id}/roofStructure`).catch(() => {});
    },
    async handleResponseAction(payload: any) {
      const { data } = payload;
      switch (payload.name) {
        case "delete":
          this.deleteCallback(data._id);
          break;
        case "restore":
          this.selectedDocuments = [data];
          this.$modal.show("roofStructuresBulkRestoreModal");
          break;
        case "edit":
          this.$router.push(`/admin/${data._id}/roofStructure`);
          break;
        default:
          this.errorMessage = "Operation specified is not supported";
      }
    },
    bulkActionHandler(action: string) {
      switch (action) {
        case "Delete Selected":
          if (!this.selectedDocuments.length) {
            this.errorMessage = "No roof structures selected.";
            return;
          }
          this.$modal.show("roofStructuresBulkDeleteModal");
          break;
        case "Restore Selected":
          if (!this.selectedDocuments.length) {
            this.errorMessage = "No roof structures selected.";
            return;
          }
          this.$modal.show("roofStructuresBulkRestoreModal");
          break;
      }
    },
    async doDeleteBulkRoofStructures(): Promise<void> {
      try {
        this.loadingText = "Deleting selected roof structures. Please wait...";
        const roofStructureIds = this.selectedDocuments.map(
          (doc: any) => doc._id
        );
        await this.deleteBulkRoofStructures(roofStructureIds);
        this.$appNotifySuccess(
          "Selected roof structures deleted successfully."
        );
        this.selectedDocuments = [];
        this.fetchPageData({});
      } catch (e) {
        this.errorMessage = e.message;
        this.$bugSnagClient.notify(e);
      } finally {
        this.$modal.hide("roofStructuresBulkDeleteModal");
        this.loadingText = "";
      }
    },
    async doRestoreBulkRoofStructures(): Promise<void> {
      try {
        this.loadingText = "Restoring selected roof structures. Please wait...";
        const roofStructureIds = this.selectedDocuments.map(
          (doc: any) => doc._id
        );
        await this.restoreBulkRoofStructures(roofStructureIds);
        this.$appNotifySuccess(
          "Selected roof structures restored successfully."
        );
        this.selectedDocuments = [];
        this.fetchPageData({});
      } catch (e) {
        this.errorMessage = e.message;
        this.$bugSnagClient.notify(e);
      } finally {
        this.$modal.hide("roofStructuresBulkRestoreModal");
        this.loadingText = "";
      }
    },
    async deleteOneRoofStructure(): Promise<void> {
      try {
        this.isLoading = true;
        this.loadingText = "Deleting roof structure. Please wait...";
        await this.deleteRoofStructure(this.roofStructureId);
        this.$modal.hide("roofStructureDeleteModal");
        this.$appNotifySuccess("Roof Structure Successfully Deleted");
      } catch (e) {
        this.errorMessage = e.message;
        this.$bugSnagClient.notify(e);
      } finally {
        this.isLoading = false;
        this.loadingText = "";
      }
    },
    deleteCallback(id: string): void {
      this.roofStructureId = id;
      this.$modal.show("roofStructureDeleteModal");
    }
  },
  computed: {
    ...roofStructureMapGetters([
      "roofStructures",
      "getTotalRecords",
      "getRoofStructuresLinks"
    ]),
    tableColumns(): ITableWrapperColumns {
      return cloneDeep(roofStructureTableColumns);
    },
    viewingRecycleBin(): boolean {
      return this.queryOverride.deleted === true;
    },
    tableRowActions(): IOptions[] {
      if (this.viewingRecycleBin) {
        const actions: IOptions[] = [];
        if (this.$isCurrentUserAdmin) {
          actions.push({
            label: "Restore",
            value: "restore",
            faIcon: "trash-restore",
            class: "bg-blue-400 text-white hover:text-white hover:bg-blue-500"
          });
        }
        return actions;
      }

      const actions: IOptions[] = [
        { label: "Edit", value: "edit", faIcon: "edit" }
      ];

      if (this.$isCurrentUserAdmin) {
        actions.push({
          label: "Delete",
          value: "delete",
          faIcon: "trash-alt",
          class: "bg-red-400 text-white hover:text-white hover:bg-red-500"
        });
      }

      return actions;
    },
    bulkMenuActions(): { title: string; disabled: boolean }[] {
      if (!this.$isCurrentUserAdmin) return [];
      return [
        {
          title: this.viewingRecycleBin
            ? "Restore Selected"
            : "Delete Selected",
          disabled: false
        }
      ];
    }
  },
  mounted() {
    this.fetchPageData({});
  }
});
